import React, { ReactNode, useContext, useCallback, forwardRef, FC, HTMLProps } from 'react';
import { ThemeContext } from 'styled-components';
import { SC } from 'ia-react-core/~/interfaces/SC';
import { SCP } from 'ia-react-core/~/interfaces/SCP';
import { WithAnalytics } from 'ia-react-core/~/interfaces/WithAnalytics';
import { ButtonProps } from '../_Button';

import {
  StyledButton,
  StyledLoaderWrapper,
  StyledLoader,
  StyledLeftIcon,
  StyledRightIcon,
  StyledLeftMostIcon,
} from './PrimaryButton.styles';

export interface PrimaryButtonProps extends WithAnalytics, ButtonProps {
  /** The button content */
  children: ReactNode;
  /** Deativates the button and displays a loader */
  busy?: boolean;
  /** Icon to display most left of the button */
  isLeftMostIcon?: boolean;
  /** Icon to display left of the button */
  LeftIcon?: SC<'svg'> | FC<{} & HTMLProps<SVGElement>>;
  /** Icon to display right of the button */
  RightIcon?: SC<'svg'> | FC<{} & HTMLProps<SVGElement>>;
  /** Reverses the colors of the button. */
  reversed?: boolean;
  /** Stretch the button to the full width. */
  fullWidth?: boolean;
  /** Disables the button. */
  disabled?: boolean;
}

const PrimaryButtonButtonDefaultProps = {
  disabled: false,
  busy: false,
  wrapText: false,
};

/**
 * @since 0.19.0
 */
const PrimaryButton = forwardRef<HTMLButtonElement, PrimaryButtonProps>(({
  as, children, disabled, busy, LeftIcon, RightIcon, isLeftMostIcon, wrapText, ...props
}, ref) => {
  const theme = useContext(ThemeContext);
  const FilteredLeftIcon = useCallback(({ className }: SCP<'svg'>) => <LeftIcon className={className} />, [LeftIcon]);
  const FilteredRightIcon = useCallback(({ className }: SCP<'svg'>) => <RightIcon className={className} />, [RightIcon]);

  const LeftMostIconComponent = isLeftMostIcon ?
    <StyledLeftMostIcon isLeftMostIcon busy={busy} as={FilteredLeftIcon as any} /> :
    <StyledLeftIcon busy={busy} as={FilteredLeftIcon as any} />;

  return (
    <StyledButton
      type={(!as && 'button') || undefined}
      tag={as}
      isPhoneSize={isLeftMostIcon}
      busy={busy}
      $wrap={wrapText}
      wrapText={wrapText}
      disabled={disabled || busy}
      data-testid="PrimaryButton"
      {...props}
      ref={ref}
    >
      { LeftIcon ? LeftMostIconComponent : null}
      {busy && (
        <StyledLoaderWrapper>
          <StyledLoader size={28} reversed={theme?.PrimaryButton?.reversedLoader} />
        </StyledLoaderWrapper>
      )}
      {children}
      {RightIcon && <StyledRightIcon busy={busy} as={FilteredRightIcon} />}
    </StyledButton>
  );
});

PrimaryButton.displayName = 'PrimaryButton';
PrimaryButton.defaultProps = PrimaryButtonButtonDefaultProps;

export default PrimaryButton;
